import { AfterViewInit, Component, computed, DestroyRef, ElementRef, HostListener, inject, Signal, ViewChild } from '@angular/core';
import { TopbarComponent } from '../../components/minisite/topbar/topbar.component';
import { FooterComponent } from '../../components/minisite/footer/footer.component';
import { OverlayComponent } from '@components/minisite/overlay/overlay.component';
import { DvmService } from '@services/dvm.service';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';
import { Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { PortalService } from '@services/portal.service';
import { CdkPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { MenuService } from '@services/components/menu.service';
import { BottomSheetComponent } from '@components/minisite/footer/bottom-sheet/bottom-sheet.component';
import { BottomSheetService } from '@services/components/bottom-sheet.service';
import { SearchService } from '@services/components/search.service';
import { BenefitsService } from '@services/benefits.service';
import { MenuContainerService, MenuFactoryService, MenuParamsService, MenuSelectionService } from '@services/menu';
import { VenueLayoutService } from '@services/venue-layout.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'app-viewer-layout',
    imports: [
        TopbarComponent,
        OverlayComponent,
        FooterComponent,
        BottomSheetComponent,
        CdkPortalOutlet
    ],
    viewProviders: [
        MenuService,
        MenuContainerService,
        MenuFactoryService,
        MenuParamsService,
        MenuSelectionService,
        SearchService,
        BenefitsService,
        VenueLayoutService,
    ],
    templateUrl: './viewer-layout.component.html',
    styleUrl: './viewer-layout.component.css'
})
export class ViewerLayoutComponent implements AfterViewInit {

  // VIEW CHILDS
  @ViewChild('mainContainer', { static: true })
  private readonly mainContainer!: ElementRef<HTMLElement>;

  // HOST LISTENERS
  @HostListener('document:pointerdown', ['$event'])
  @HostListener('document:focusin', ['$event'])
  public trackKeyboardTarget(event: Event): void {
    const target = event.target;
    this.keyboardTarget = target instanceof Element && this.mainContainer.nativeElement.contains(target) ? target : null;
  }

  @HostListener('document:keydown', ['$event'])
  public handleMapKeyDown(event: KeyboardEvent): void {
    if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', '+', '=', '-'].includes(event.key) ||
        this.portalContent() || this.bottomSheet.state() !== 'closed') return;

    // Disabling a toolbar button during animation can move focus back to the body.
    const target = event.target === this.mainContainer.nativeElement.ownerDocument.body ? this.keyboardTarget : event.target;
    if (!(target instanceof Element) || !this.mainContainer.nativeElement.contains(target) || target.closest(
      'input, textarea, select, [contenteditable]:not([contenteditable="false"]), ' +
      '[role="menu"], [role="menubar"], [role="listbox"], [role="combobox"], [role="slider"], ' +
      '[role="spinbutton"], [role="tree"], [role="grid"], [role="tablist"], [role="dialog"], ' +
      'app-venue-layout-container, app-benefits-container, app-cookies, .legend-panel'
    )) return;

    for (let element: Element | null = target; element && element !== this.mainContainer.nativeElement; element = element.parentElement) {
      const style = getComputedStyle(element);
      if ((/(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight) ||
          (/(auto|scroll)/.test(style.overflowX) && element.scrollWidth > element.clientWidth)) return;
    }

    this.dvmService.handleMapKeyDown(event);
  }

  @HostListener('window:keydown', ['$event'])
  public handleFullScreen(event: KeyboardEvent): void {
    
    if (event.key === 'F11' && this.view() === AppViews.Pano) {
      event.preventDefault();
      this.dvmService.viewer3d.toggleFullscreen();
    }

  }

  // SERVICES
  private readonly dvmService:     DvmService              = inject(DvmService);
  private readonly portalService:  PortalService           = inject(PortalService);
  private readonly bottomSheet:    BottomSheetService      = inject(BottomSheetService);
  private readonly destroyRef:     DestroyRef              = inject(DestroyRef);

  // GLOBAL STATE
  private readonly view:           Signal<AppViews | null> = this.dvmService.view;

  // COMPONENT STATE
  private keyboardTarget: Element | null = null;
  public readonly  portalContent:  Signal<ComponentPortal<unknown>|null> = this.portalService.portalContent;
  public readonly  hideGradients:  Signal<boolean>          = computed((): boolean => this.view() === AppViews.Pano);

  // LIFECYCLE HOOKS
  ngAfterViewInit(): void {
    this.initComponent();
  }

  // METHODS
  /**
   * Inicializador del layout del viewer.
   */
  private initComponent(): void {

    this.overrideFullscreen();
    this.closeBottomSheetOnDestroy();

    // init methods para el viewer layout...

  }


  /**
   * El bottom sheet es root y sobrevive al layout: si se sale del viewer con el abierto, el
   * siguiente montaria un sheet ya abierto y vacio.
   */
  private closeBottomSheetOnDestroy(): void {

    this.destroyRef.onDestroy((): void => {
      if(this.bottomSheet.state() !== 'closed') this.bottomSheet.close();
    });

  }

  /**
   * Sobreescribe el elemento del fullscreen del viewer.
   */
  private overrideFullscreen(): void {

    const viewer3d: Viewer3dService = this.dvmService.viewer3d;

    viewer3d.waitInitialize()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((): void => viewer3d.interface.overrideFullscreenElement(this.mainContainer.nativeElement));

  }

}
